/**
 * Switching between the text and the preview with a swipe on a narrow screen (decision 12; part 15, table S). Only a
 * finger counts (a mouse or pen drag selects text), only a quick, mostly sideways stroke of some length, and never one
 * that starts at a side of the screen, where the system's own back and forward gestures begin. Both panes let the
 * browser pan only vertically (`touch-action: pan-y`), so a sideways stroke reaches the page as pointer events instead
 * of being taken as a pan and cancelled.
 */
import type { NarrowPane } from '../shell/layout.ts';

/** S1: the shortest stroke that switches, in CSS pixels. */
export const SWIPE_DISTANCE = 64;
/** S6: a stroke starting this close to either side of the pane is the system's (back or forward), in CSS pixels. */
export const EDGE = 24;
/** S7: the longest a swipe takes, in milliseconds; a slower stroke is a finger held to select or scroll. */
export const SWIPE_MS = 500;

/** A finished stroke over a pane: CSS pixels from the pane's left and top, the pane's width, and its times. */
export interface Stroke {
  readonly pointer: string;
  readonly startX: number;
  readonly startY: number;
  readonly endX: number;
  readonly endY: number;
  readonly width: number;
  readonly startTime: number;
  readonly endTime: number;
}

/** S1–S7: the pane a stroke over `from` switches to, or null when it doesn't switch. */
export function swipeTo(from: NarrowPane, stroke: Stroke): NarrowPane | null {
  if (stroke.pointer !== 'touch') return null;
  const dx = stroke.endX - stroke.startX;
  const dy = stroke.endY - stroke.startY;
  // S1, S2: long enough, and at least twice as far sideways as up or down.
  if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) < 2 * Math.abs(dy)) return null;
  // S6: not from a side, which the system's gestures own.
  if (stroke.startX < EDGE || stroke.startX > stroke.width - EDGE) return null;
  // S7: quick.
  if (stroke.endTime - stroke.startTime > SWIPE_MS) return null;
  // S3, S4: leftward from the text to the preview, rightward back.
  if (from === 'text') return dx < 0 ? 'preview' : null;
  return dx > 0 ? 'text' : null;
}
